// src/tabs/ConciliacaoBancariaSubTab.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { 
  Upload, 
  Activity, 
  Check, 
  X, 
  HelpCircle, 
  AlertCircle, 
  Search, 
  FolderOpen,
  ArrowRightLeft,
  Info,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  ChevronLeft,
  ChevronRight,
  BookOpen
} from "lucide-react";
import { conciliacaoService } from "../services/conciliacaoService";
import { pb } from "../services/pocketbase";
import { formatCentavos, parseCentavos } from "../utils/moneyUtils";

export default function ConciliacaoBancariaSubTab({ finance }) {
  const [loading, setLoading] = useState(false);
  const [lotes, setLotes] = useState([]);
  const [activeLote, setActiveLote] = useState(null);
  
  // Fila de transações do lote ativo
  const [transacoes, setTransacoes] = useState([]);
  const [filterStatus, setFilterStatus] = useState("nao_conciliado"); // nao_conciliado, conciliado, ignorado
  const [activeSubTab, setActiveSubTab] = useState("extrato"); // extrato, reversa

  // Matches cache por transacao ID
  const [matchesCache, setMatchesCache] = useState({});

  // Paginação da fila do extrato
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Modais e formulários
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [isIgnoreModalOpen, setIsIgnoreModalOpen] = useState(false);
  const [isPendenciaModalOpen, setIsPendenciaModalOpen] = useState(false);
  
  const [selectedTx, setSelectedTx] = useState(null);
  const [ignoreReason, setIgnoreReason] = useState("");
  const [pendenciaCategoria, setPendenciaCategoria] = useState("");
  const [pendenciaCentro, setPendenciaCentro] = useState("");

  // Busca manual de Lançamentos Ledger
  const [manualSearchQuery, setManualSearchQuery] = useState("");
  const [manualCandidates, setManualCandidates] = useState([]);
  const [manualLoading, setManualLoading] = useState(false);

  // Lançamentos não conciliados (Pendências Reversas)
  const [reversas, setReversas] = useState([]);

  // Estados da Conciliação de Duas Colunas N:M (R5, R6)
  const [ledgerPendentes, setLedgerPendentes] = useState([]);
  const [selectedTxIds, setSelectedTxIds] = useState([]);
  const [selectedLedgerIds, setSelectedLedgerIds] = useState([]);
  const [ledgerSearchQuery, setLedgerSearchQuery] = useState("");
  const [ledgerDateInicio, setLedgerDateInicio] = useState("");
  const [ledgerDateFim, setLedgerDateFim] = useState("");
  const [filterLedgerTipo, setFilterLedgerTipo] = useState("todos"); // todos, receita, despesa

  // Estados para as Regras por Descritor (R7)
  const [regrasDescritor, setRegrasDescritor] = useState([]);
  const [learningRule, setLearningRule] = useState(null);

  // Upload States
  const [uploadConta, setUploadConta] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadError, setUploadError] = useState("");

  // Carregar lotes em aberto
  const fetchLotes = useCallback(async () => {
    try {
      const res = await pb.collection("fin_extratos_importados").getFullList({
        sort: "-created",
        expand: "conta_bancaria,importado_por"
      });
      setLotes(res);
    } catch (err) {
      console.error("Erro ao buscar lotes de extrato:", err);
    }
  }, []);

  const fetchRegras = useCallback(async () => {
    try {
      const res = await conciliacaoService.getRegrasDescritor();
      setRegrasDescritor(res || []);
    } catch (err) {
      console.error("Erro ao buscar regras:", err);
    }
  }, []);

  useEffect(() => {
    fetchLotes();
    fetchRegras();
  }, [fetchLotes, fetchRegras]);

  // Carregar transações do lote ativo
  const fetchTransacoes = useCallback(async () => {
    if (!activeLote) return;
    setLoading(true);
    try {
      let items = [];
      if (filterStatus === "nao_conciliado") {
        const res = await pb.collection("fin_extrato_transacoes").getFullList({
          filter: `lote = "${activeLote.id}" && status = "${filterStatus}"`,
          sort: "data, fitid",
          expand: "lancamento_vinculado,lancamento_vinculado.categoria,lancamento_vinculado.centro_custo"
        });
        items = res || [];
        setTotalPages(1);
        setTotalItems(items.length);
      } else {
        const res = await pb.collection("fin_extrato_transacoes").getList(page, 20, {
          filter: `lote = "${activeLote.id}" && status = "${filterStatus}"`,
          sort: "data, fitid",
          expand: "lancamento_vinculado,lancamento_vinculado.categoria,lancamento_vinculado.centro_custo"
        });
        items = res.items || [];
        setTotalPages(res.totalPages || 1);
        setTotalItems(res.totalItems || 0);
      }
      setTransacoes(items);

      // Limpa cache de matches e carrega para cada transação não conciliada
      const newCache = {};
      if (filterStatus === "nao_conciliado") {
        await Promise.all(
          items.map(async (tx) => {
            const matches = await conciliacaoService.getMatchesForTransaction(tx);
            newCache[tx.id] = matches;
          })
        );
      }
      setMatchesCache(newCache);
    } catch (err) {
      console.error("Erro ao buscar transações do lote:", err);
    } finally {
      setLoading(false);
    }
  }, [activeLote, page, filterStatus]);

  useEffect(() => {
    fetchTransacoes();
  }, [fetchTransacoes]);

  // Carregar pendências reversas (Lançamentos no sistema sem extrato)
  const fetchReversas = useCallback(async () => {
    if (!activeLote) return;
    try {
      const filter = `conta_bancaria = "${activeLote.conta_bancaria}" && data >= "${activeLote.periodo_inicio}" && data <= "${activeLote.periodo_fim}" && transacao_bancaria = null && status = "pago"`;
      const res = await pb.collection("fin_lancamentos").getFullList({
        filter,
        sort: "data",
        expand: "categoria,centro_custo"
      });
      setReversas(res);
    } catch (err) {
      console.error("Erro ao carregar pendências reversas:", err);
    }
  }, [activeLote]);

  useEffect(() => {
    if (activeSubTab === "reversa") {
      fetchReversas();
    }
  }, [activeSubTab, fetchReversas]);

  const fetchLedgerPendentes = useCallback(async () => {
    if (!activeLote) return;
    try {
      let filterParts = [
        `(status = "pendente_conciliacao" || status = "a_vencer" || status = "provisao")`,
        `(conta_bancaria = null || conta_bancaria = "${activeLote.conta_bancaria}")`
      ];

      if (ledgerSearchQuery.trim()) {
        filterParts.push(`descricao ~ "${ledgerSearchQuery}"`);
      }
      if (ledgerDateInicio) {
        filterParts.push(`data >= "${ledgerDateInicio}"`);
      }
      if (ledgerDateFim) {
        filterParts.push(`data <= "${ledgerDateFim}"`);
      }
      if (filterLedgerTipo === "receita") {
        filterParts.push("valor_centavos > 0");
      } else if (filterLedgerTipo === "despesa") {
        filterParts.push("valor_centavos < 0");
      }

      const filterStr = filterParts.join(" && ");
      const res = await pb.collection("fin_lancamentos").getFullList({
        filter: filterStr,
        sort: "data",
        expand: "categoria,centro_custo"
      });
      setLedgerPendentes(res || []);
    } catch (err) {
      console.warn("Erro ao buscar ledger pendentes:", err.message);
    }
  }, [activeLote, ledgerSearchQuery, ledgerDateInicio, ledgerDateFim, filterLedgerTipo]);

  useEffect(() => {
    if (activeLote && activeSubTab === "extrato") {
      fetchLedgerPendentes();
    }
  }, [fetchLedgerPendentes, activeSubTab, activeLote]);

  // Resetar página ao mudar filtros
  useEffect(() => {
    setPage(1);
  }, [filterStatus]);

  // Handler para Upload de OFX
  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    setUploadError("");
    if (!uploadConta) {
      setUploadError("Por favor, selecione a conta bancária correspondente.");
      return;
    }
    if (!uploadFile) {
      setUploadError("Por favor, selecione um arquivo OFX.");
      return;
    }

    setLoading(true);
    try {
      const text = await uploadFile.text();
      const parsed = conciliacaoService.parseOFX(text);

      if (parsed.transactions.length === 0) {
        throw new Error("Nenhuma transação financeira detectada no arquivo OFX.");
      }

      const summary = await conciliacaoService.importExtrato(
        uploadConta,
        uploadFile.name,
        parsed.transactions,
        pb.authStore.model
      );

      alert(
        `Importação de Extrato Concluída!\n\n` +
        `Total de Transações: ${summary.total}\n` +
        `Novas importadas: ${summary.newCount}\n` +
        `Ignoradas por duplicidade: ${summary.skippedCount}`
      );

      setUploadFile(null);
      await fetchLotes();

      if (summary.loteId) {
        const novoLote = await pb.collection("fin_extratos_importados").getOne(summary.loteId, {
          expand: "conta_bancaria"
        });
        setActiveLote(novoLote);
        setFilterStatus("nao_conciliado");
      }
    } catch (err) {
      setUploadError(err.message || "Erro desconhecido ao processar extrato.");
    } finally {
      setLoading(false);
    }
  };

  // Executar Conciliação Rápida (Match Perfeito)
  const handleConciliarMatch = async (txId, lancamentoId) => {
    try {
      const tx = transacoes.find(t => t.id === txId);
      const lanc = await pb.collection("fin_lancamentos").getOne(lancamentoId);

      await conciliacaoService.vincularTransacao(txId, lancamentoId, pb.authStore.model);
      await fetchTransacoes();
      if (activeLote) {
        // Atualiza cabeçalho do lote ativo
        const updatedLote = await pb.collection("fin_extratos_importados").getOne(activeLote.id, {
          expand: "conta_bancaria"
        });
        setActiveLote(updatedLote);
      }

      // Propose learning rule if applicable
      if (tx && lanc && lanc.categoria) {
        detectAndSuggestRule(tx, lanc.categoria);
      }
    } catch (err) {
      alert("Erro ao realizar conciliação: " + err.message);
    }
  };

  // Executar Conciliação de Grupo (N:M) (R5)
  const handleConciliarGrupoSubmit = async () => {
    if (selectedTxIds.length === 0) {
      alert("Selecione pelo menos uma transação bancária.");
      return;
    }
    if (selectedLedgerIds.length === 0) {
      alert("Selecione pelo menos um lançamento do Ledger.");
      return;
    }

    // Calcula somatórios
    let somaTransacoes = 0;
    transacoes.forEach(t => {
      if (selectedTxIds.includes(t.id)) {
        somaTransacoes += (t.valor_centavos || 0);
      }
    });

    let somaLedger = 0;
    ledgerPendentes.forEach(l => {
      if (selectedLedgerIds.includes(l.id)) {
        somaLedger += (l.valor_centavos || 0);
      }
    });

    const diff = somaLedger - somaTransacoes;

    if (diff < 0) {
      alert(`Valor selecionado no Ledger (R$ ${(somaLedger/100).toFixed(2)}) é menor que o das transações do banco (R$ ${(somaTransacoes/100).toFixed(2)}). A diferença matemática deve ser zero ou positiva (representando tarifas/taxas cobradas).`);
      return;
    }

    const confirmMsg = `Confirmar conciliação de grupo?\n\n` +
      `Soma Transações Extrato: R$ ${(somaTransacoes/100).toFixed(2)}\n` +
      `Soma Lançamentos Ledger: R$ ${(somaLedger/100).toFixed(2)}\n` +
      `Taxa de Ajuste / Tarifa: R$ ${(diff/100).toFixed(2)}`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    setLoading(true);
    try {
      await conciliacaoService.conciliarGrupo(selectedTxIds, selectedLedgerIds, diff, pb.authStore.model);
      alert("Conciliação de grupo concluída com sucesso!");
      
      // Limpa seleções
      setSelectedTxIds([]);
      setSelectedLedgerIds([]);
      
      // Recarrega dados
      await fetchTransacoes();
      await fetchLedgerPendentes();
      
      if (typeof finance.refreshSaldos === "function") {
        await finance.refreshSaldos();
      }
      if (activeLote) {
        const updatedLote = await pb.collection("fin_extratos_importados").getOne(activeLote.id, {
          expand: "conta_bancaria"
        });
        setActiveLote(updatedLote);
      }

      // Propose learning rule if applicable for first selected transaction
      const txExemplo = transacoes.find(t => selectedTxIds.includes(t.id));
      const lcExemplo = ledgerPendentes.find(l => selectedLedgerIds.includes(l.id));
      if (txExemplo && lcExemplo && lcExemplo.categoria) {
        detectAndSuggestRule(txExemplo, lcExemplo.categoria);
      }
    } catch (err) {
      alert("Erro ao realizar conciliação de grupo: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── REGISTRO DE REGRAS APRENDIDAS (R7) ─────────────────────────────
  const detectAndSuggestRule = (tx, categoryId) => {
    if (!tx || !categoryId) return;
    const normalizedDesc = tx.descricao.toUpperCase();
    const hasRule = regrasDescritor.some(r => normalizedDesc.includes(r.padrao.toUpperCase()));
    if (hasRule) return;

    const cleanDesc = tx.descricao
      .replace(/[0-9]+/g, "")
      .replace(/\s+/g, " ")
      .trim();

    const words = cleanDesc.split(" ");
    let patternCandidate = words.slice(0, 3).join(" ");
    if (!patternCandidate) {
      patternCandidate = tx.descricao.slice(0, 15);
    }

    const cat = finance.categorias.find(c => c.id === categoryId);
    const catNome = cat ? cat.nome : "Categoria";
    const catTipo = cat ? cat.tipo : "receita";

    setLearningRule({
      txId: tx.id,
      descricaoOriginal: tx.descricao,
      padrao: patternCandidate.toUpperCase(),
      categoriaId: categoryId,
      categoriaNome: catNome,
      tipo: catTipo === "despesa" ? "tarifa" : "receita_cartao"
    });
  };

  const handleSaveLearnedRule = async () => {
    if (!learningRule) return;
    setLoading(true);
    try {
      await conciliacaoService.criarRegraDescritor(
        learningRule.padrao,
        learningRule.categoriaId,
        learningRule.tipo,
        "aprendida",
        activeLote ? activeLote.conta_bancaria : null
      );
      alert("Regra de descritor memorizada com sucesso!");
      setLearningRule(null);
      await fetchRegras();
    } catch (err) {
      alert("Erro ao salvar regra: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Ignorar transação
  const handleIgnorarSubmit = async (e) => {
    e.preventDefault();
    if (!ignoreReason.trim()) {
      alert("Informe o motivo do descarte.");
      return;
    }
    try {
      await conciliacaoService.ignorarTransacao(selectedTx.id, ignoreReason, pb.authStore.model);
      setIsIgnoreModalOpen(false);
      setIgnoreReason("");
      setSelectedTx(null);
      await fetchTransacoes();
      if (activeLote) {
        const updatedLote = await pb.collection("fin_extratos_importados").getOne(activeLote.id, {
          expand: "conta_bancaria"
        });
        setActiveLote(updatedLote);
      }
    } catch (err) {
      alert("Erro ao ignorar transação: " + err.message);
    }
  };

  // Gerar Pendência
  const handlePendenciaSubmit = async (e) => {
    e.preventDefault();
    if (!pendenciaCategoria || !pendenciaCentro) {
      alert("Selecione a categoria e o centro de custo sugeridos.");
      return;
    }
    try {
      await conciliacaoService.criarPendenciaDaTransacao(
        selectedTx.id,
        pendenciaCategoria,
        pendenciaCentro,
        pb.authStore.model
      );
      setIsPendenciaModalOpen(false);
      setPendenciaCategoria("");
      setPendenciaCentro("");
      setSelectedTx(null);
      await fetchTransacoes();
      
      // Notifica o menu superior sobre novas pendências criadas
      if (typeof finance.refreshPendingCount === "function") {
        finance.refreshPendingCount();
      }
      
      if (activeLote) {
        const updatedLote = await pb.collection("fin_extratos_importados").getOne(activeLote.id, {
          expand: "conta_bancaria"
        });
        setActiveLote(updatedLote);
      }
      alert("Transação enviada com sucesso para a Fila de Conferência Financeira!");
    } catch (err) {
      alert("Erro ao enviar transação: " + err.message);
    }
  };

  // Busca manual de lançamentos
  const handleManualSearch = async () => {
    setManualLoading(true);
    try {
      let filter = `conta_bancaria = "${activeLote.conta_bancaria}" && transacao_bancaria = null`;
      if (manualSearchQuery.trim()) {
        filter += ` && (descricao ~ "${manualSearchQuery}" || valor_centavos = ${parseCentavos(manualSearchQuery) || 0})`;
      }
      const res = await pb.collection("fin_lancamentos").getList(1, 15, {
        filter,
        sort: "-data",
        expand: "categoria,centro_custo"
      });
      setManualCandidates(res.items || []);
    } catch (err) {
      console.error("Erro ao buscar lançamentos manual:", err);
    } finally {
      setManualLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-slate-50 relative">
      
      {/* ─── LOADER DE OPERAÇÃO GLOBAL ────────────────────────────────── */}
      {loading && (
        <div className="absolute inset-0 bg-slate-900/25 backdrop-blur-[1.5px] flex items-center justify-center z-50 pointer-events-auto">
          <div className="bg-white p-5 rounded-2xl shadow-2xl border border-slate-100 flex flex-col items-center gap-3 animate-fade-in max-w-xs text-center">
            <Activity className="animate-spin text-blue-600" size={32} />
            <span className="font-black text-xs text-slate-800 uppercase tracking-wider">Processando Lote / Extrato</span>
            <span className="text-[10px] font-bold text-slate-450">Pesquisando transações e cruzando dados de idempotência...</span>
          </div>
        </div>
      )}
      
      {/* ─── CASO 1: SEM LOTE ATIVO SELECIONADO (TELA DE UPLOAD & HISTÓRICO) ───────────────── */}
      {!activeLote ? (
        <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-6 max-w-5xl mx-auto w-full">
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Box de Upload */}
            <div className="md:col-span-1 bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
              <div className="flex items-center gap-2 border-b border-slate-100 pb-3 mb-2">
                <Upload className="text-blue-600" size={18} />
                <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider">Novo Extrato OFX</h3>
              </div>

              <form onSubmit={handleUploadSubmit} className="space-y-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Conta Bancária *</label>
                  <select
                    required
                    value={uploadConta}
                    onChange={(e) => setUploadConta(e.target.value)}
                    className="w-full text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-2 outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Selecione a Conta...</option>
                    {finance.contasBancarias.map((c) => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Arquivo Extrato *</label>
                  <input
                    type="file"
                    required
                    accept=".ofx"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    className="w-full text-xs text-slate-500 border border-slate-200 rounded-lg p-2 bg-slate-50 cursor-pointer outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <span className="text-[9px] text-slate-400 block mt-1">Selecione arquivos no formato `.ofx` exportados pelo banco.</span>
                </div>

                {uploadError && (
                  <div className="flex gap-1.5 bg-red-50 border border-red-200 text-red-700 p-2.5 rounded-lg text-[10px] font-bold">
                    <AlertCircle size={14} className="shrink-0" />
                    <span>{uploadError}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg text-xs font-bold shadow transition-colors flex items-center justify-center gap-1.5"
                >
                  <Activity size={15} /> Processar Extrato
                </button>
              </form>
            </div>

            {/* Lotes em Aberto */}
            <div className="md:col-span-2 bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex flex-col">
              <div className="flex items-center gap-2 border-b border-slate-100 pb-3 mb-4">
                <FolderOpen className="text-slate-500" size={18} />
                <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider">Histórico / Lotes em Conciliação</h3>
              </div>

              <div className="flex-1 overflow-y-auto space-y-3 min-h-[300px]">
                {lotes.map((lote) => {
                  const conciliadasPct = lote.total_transacoes > 0 
                    ? Math.round(((lote.total_conciliadas + lote.total_ignoradas) / lote.total_transacoes) * 100) 
                    : 0;

                  return (
                    <div 
                      key={lote.id} 
                      onClick={() => {
                        setActiveLote(lote);
                        setFilterStatus("nao_conciliado");
                      }}
                      className="border border-slate-200 hover:border-blue-500 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 cursor-pointer hover:bg-slate-50/50 transition-all select-none"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-black text-slate-800">
                            {lote.expand?.conta_bancaria?.nome || "Sem Conta"}
                          </span>
                          <span className="text-[9px] font-black uppercase bg-slate-100 text-slate-500 px-2 py-0.5 rounded border border-slate-200 font-mono">
                            {lote.formato}
                          </span>
                        </div>
                        <p className="text-[10px] font-bold text-slate-400 font-mono">
                          Ref: {lote.nome_arquivo} | Período: {lote.periodo_inicio} até {lote.periodo_fim}
                        </p>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-xs font-black text-slate-700">
                            {lote.total_conciliadas + lote.total_ignoradas} / {lote.total_transacoes}
                          </div>
                          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Transações Conciliadas</span>
                        </div>

                        <div className="w-16 bg-slate-100 rounded-full h-2 overflow-hidden border border-slate-200">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              conciliadasPct === 100 ? "bg-emerald-600" : "bg-blue-600"
                            }`}
                            style={{ width: `${conciliadasPct}%` }}
                          />
                        </div>

                        <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded border ${
                          conciliadasPct === 100 
                            ? "bg-emerald-50 text-emerald-700 border-emerald-250" 
                            : "bg-amber-50 text-amber-700 border-amber-250"
                        }`}>
                          {conciliadasPct === 100 ? "Conciliado" : `${conciliadasPct}%`}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {lotes.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 gap-2 text-slate-400">
                    <Activity size={24} className="animate-pulse" />
                    <span className="text-xs font-semibold">Nenhum extrato importado ainda.</span>
                  </div>
                )}
              </div>
            </div>

          </div>

        </div>
      ) : (
        
        // ─── CASO 2: COM LOTE ATIVO SELECIONADO (PAINEL DE CONCILIAÇÃO) ───────────────────
        <div className="flex-1 flex flex-col overflow-hidden">
          
          {/* HEADER DO LOTE */}
          <div className="bg-white border-b border-slate-200 p-4 shrink-0 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setActiveLote(null)}
                  className="text-xs font-bold text-slate-500 hover:text-slate-800 bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200 transition-colors"
                >
                  Voltar
                </button>
                <h3 className="text-sm font-black text-slate-800">
                  {activeLote.expand?.conta_bancaria?.nome || "Sem Conta"}
                </h3>
              </div>
              <p className="text-[10px] font-bold text-slate-400 font-mono">
                Lote: {activeLote.nome_arquivo} | Período: {activeLote.periodo_inicio} até {activeLote.periodo_fim}
              </p>
            </div>

            {/* Estatísticas Lote */}
            <div className="flex items-center gap-6">
              <div className="text-right">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Total Transações</span>
                <span className="text-sm font-black text-slate-700">{activeLote.total_transacoes}</span>
              </div>
              <div className="text-right">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Conciliadas</span>
                <span className="text-sm font-black text-emerald-700">{activeLote.total_conciliadas}</span>
              </div>
              <div className="text-right">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Ignoradas</span>
                <span className="text-sm font-black text-amber-700">{activeLote.total_ignoradas}</span>
              </div>
              <div className="w-24 bg-slate-100 rounded-full h-2.5 overflow-hidden border border-slate-200">
                <div 
                  className="bg-blue-600 h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${activeLote.total_transacoes > 0 
                      ? Math.round(((activeLote.total_conciliadas + activeLote.total_ignoradas) / activeLote.total_transacoes) * 100) 
                      : 0}%`
                  }}
                />
              </div>
            </div>
          </div>

          {/* SELETOR DE SUB-ABAS INTERNAS */}
          <div className="bg-white border-b border-slate-200 px-4 flex justify-between items-center shrink-0">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveSubTab("extrato")}
                className={`flex items-center gap-1.5 px-4 py-2 border-b-2 font-bold text-xs uppercase tracking-wider transition-colors ${
                  activeSubTab === "extrato"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                <Activity size={14} /> Fila do Extrato
              </button>
              <button
                onClick={() => setActiveSubTab("reversa")}
                className={`flex items-center gap-1.5 px-4 py-2 border-b-2 font-bold text-xs uppercase tracking-wider transition-colors ${
                  activeSubTab === "reversa"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                <ArrowRightLeft size={14} /> Pendências Reversas
              </button>
            </div>

            {/* Filtros da fila do extrato */}
            {activeSubTab === "extrato" && (
              <div className="flex items-center gap-1.5 py-1">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide">Exibir:</span>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1 outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="nao_conciliado">Não Conciliadas</option>
                  <option value="conciliado">Conciliadas</option>
                  <option value="ignorado">Ignoradas</option>
                </select>
              </div>
            )}
          </div>

          {/* CONTEÚDO DA FILA DO EXTRATO */}
          {activeSubTab === "extrato" && (
            <div className="flex-1 flex flex-col overflow-hidden">
              
              {filterStatus === "nao_conciliado" ? (
                // ─── NOVO LAYOUT DE DUAS COLUNAS PARA CONCILIAÇÃO N:M (R6) ───
                <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
                  
                  {/* COLUNA DA ESQUERDA: LEDGER (LANÇAMENTOS) */}
                  <div className="flex-1 flex flex-col border-r border-slate-200 bg-white overflow-hidden">
                    <div className="p-3 bg-slate-50/80 border-b border-slate-200 flex flex-col gap-2 shrink-0">
                      <div className="flex justify-between items-center">
                        <h4 className="text-xs font-black text-slate-700 uppercase tracking-wider">Ledger (Lançamentos)</h4>
                        <span className="text-[10px] font-bold text-slate-500 font-mono">
                          {ledgerPendentes.length} pendentes
                        </span>
                      </div>
                      
                      {/* Filtros da Coluna Ledger */}
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          placeholder="Buscar descrição..."
                          value={ledgerSearchQuery}
                          onChange={(e) => setLedgerSearchQuery(e.target.value)}
                          className="px-2 py-1 text-xs border border-slate-200 rounded outline-none"
                        />
                        <select
                          value={filterLedgerTipo}
                          onChange={(e) => setFilterLedgerTipo(e.target.value)}
                          className="px-2 py-1 text-xs border border-slate-200 rounded bg-white outline-none font-semibold text-slate-600"
                        >
                          <option value="todos">Todos Fluxos</option>
                          <option value="receita">Apenas Receitas (+)</option>
                          <option value="despesa">Apenas Despesas (-)</option>
                        </select>
                      </div>
                      
                      <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded p-1">
                        <span className="text-[9px] font-bold text-slate-400 uppercase px-1">Período:</span>
                        <input
                          type="date"
                          value={ledgerDateInicio}
                          onChange={(e) => setLedgerDateInicio(e.target.value)}
                          className="bg-transparent text-[10px] font-semibold text-slate-650 outline-none w-24"
                        />
                        <span className="text-slate-300 text-xs">/</span>
                        <input
                          type="date"
                          value={ledgerDateFim}
                          onChange={(e) => setLedgerDateFim(e.target.value)}
                          className="bg-transparent text-[10px] font-semibold text-slate-650 outline-none w-24"
                        />
                        {(ledgerDateInicio || ledgerDateFim) && (
                          <button
                            type="button"
                            onClick={() => { setLedgerDateInicio(""); setLedgerDateFim(""); }}
                            className="text-slate-400 hover:text-slate-600 ml-auto"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                      {ledgerPendentes.map((item) => {
                        const isSelected = selectedLedgerIds.includes(item.id);
                        return (
                          <div 
                            key={item.id}
                            onClick={() => {
                              setSelectedLedgerIds(prev => 
                                isSelected ? prev.filter(id => id !== item.id) : [...prev, item.id]
                              );
                            }}
                            className={`border rounded-lg p-2.5 flex items-start gap-2.5 cursor-pointer transition-all select-none ${
                              isSelected 
                                ? "border-blue-500 bg-blue-50/20 shadow-xs" 
                                : "border-slate-200 bg-white hover:bg-slate-50/50"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              readOnly
                              className="mt-0.5 w-3.5 h-3.5 text-blue-600 border-slate-350 rounded focus:ring-blue-500"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-start gap-1">
                                <span className="text-xs font-bold text-slate-800 truncate">{item.descricao}</span>
                                <span className={`text-xs font-black font-mono whitespace-nowrap ${item.valor_centavos < 0 ? "text-rose-600" : "text-emerald-700"}`}>
                                  {item.valor_centavos < 0 ? "-" : "+"} {formatCentavos(Math.abs(item.valor_centavos))}
                                </span>
                              </div>
                              <div className="flex justify-between items-center text-[10px] text-slate-400 mt-1">
                                <span className="font-mono">{item.data.split("-").reverse().join("/")}</span>
                                <span className="px-1.5 py-0.2 bg-slate-100 rounded text-[9px] font-bold">
                                  {item.expand?.categoria?.nome || "Sem Categoria"}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {ledgerPendentes.length === 0 && (
                        <div className="text-center text-xs text-slate-400 py-12">
                          Nenhum lançamento pendente encontrado.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* COLUNA DA DIREITA: EXTRATO (TRANSAÇÕES) */}
                  <div className="flex-1 flex flex-col bg-slate-50/30 overflow-hidden">
                    <div className="p-3 bg-slate-50/80 border-b border-slate-200 flex justify-between items-center shrink-0">
                      <div>
                        <h4 className="text-xs font-black text-slate-700 uppercase tracking-wider">Banco (Extrato OFX)</h4>
                        <p className="text-[10px] text-slate-400 font-medium">Selecione as transações para conciliar.</p>
                      </div>
                      <span className="text-[11px] font-bold text-slate-500 font-mono">
                        {transacoes.filter(t => t.status === "nao_conciliado").length} pendentes
                      </span>
                    </div>

                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                      
                      {/* Sugestões Automáticas Pre-pareadas no topo (R6) */}
                      {selectedTxIds.length === 0 && selectedLedgerIds.length === 0 && (
                        <div className="mb-3 space-y-2">
                          {transacoes.filter(t => t.status === "nao_conciliado").map((tx) => {
                            const candidates = matchesCache[tx.id] || [];
                            const exactMatches = candidates.filter(c => c.valor_centavos === tx.valor_centavos);
                            if (exactMatches.length !== 1) return null;
                            
                            return (
                              <div key={`sug-${tx.id}`} className="bg-emerald-50/70 border border-emerald-200 rounded-lg p-2.5 flex items-center justify-between gap-3 animate-fade-in shadow-xs">
                                <div className="min-w-0">
                                  <span className="text-[9px] font-black text-emerald-700 uppercase tracking-wider block">Match Rápido Sugerido (1:1)</span>
                                  <span className="text-xs font-bold text-slate-800 block truncate">{tx.descricao} ↔ {exactMatches[0].descricao}</span>
                                  <span className="text-[10px] text-slate-500 font-mono">Data: {tx.data} | Valor: {formatCentavos(tx.valor_centavos)}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleConciliarMatch(tx.id, exactMatches[0].id)}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[10px] uppercase tracking-wide px-3 py-1.5 rounded-md shadow-xs transition-colors shrink-0"
                                >
                                  Conciliar
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {transacoes.filter(t => t.status === "nao_conciliado").map((tx) => {
                        const isSelected = selectedTxIds.includes(tx.id);
                        const isDespesa = tx.valor_centavos < 0;
                        const matchedRule = regrasDescritor.find(r => 
                          tx.descricao.toUpperCase().includes(r.padrao.toUpperCase())
                        );

                        return (
                          <div 
                            key={tx.id}
                            onClick={() => {
                              setSelectedTxIds(prev => 
                                isSelected ? prev.filter(id => id !== tx.id) : [...prev, tx.id]
                              );
                            }}
                            className={`border rounded-lg p-2.5 flex items-start gap-2.5 cursor-pointer transition-all select-none ${
                              isSelected 
                                ? "border-blue-500 bg-blue-50/20 shadow-xs" 
                                : "border-slate-200 bg-white hover:bg-slate-50/50"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              readOnly
                              className="mt-0.5 w-3.5 h-3.5 text-blue-600 border-slate-350 rounded focus:ring-blue-500"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-start gap-1">
                                <span className="text-xs font-bold text-slate-800 truncate">{tx.descricao}</span>
                                <span className={`text-xs font-black font-mono whitespace-nowrap ${isDespesa ? "text-rose-600" : "text-emerald-700"}`}>
                                  {isDespesa ? "-" : "+"} {formatCentavos(Math.abs(tx.valor_centavos))}
                                </span>
                              </div>
                              <div className="flex justify-between items-center text-[10px] text-slate-400 mt-1 font-mono">
                                <span>{tx.data.split("-").reverse().join("/")}</span>
                                <span>FITID: {tx.fitid}</span>
                              </div>
                              {matchedRule && (
                                <div className="mt-1 flex items-center">
                                  <span className="text-[9px] font-black uppercase bg-blue-50 text-blue-700 border border-blue-150 px-2 py-0.5 rounded shadow-2xs">
                                    Recomendado: {matchedRule.expand?.categoria_sugerida?.nome || "Categoria"}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {transacoes.filter(t => t.status === "nao_conciliado").length === 0 && (
                        <div className="text-center text-xs text-slate-400 py-12">
                          Nenhuma transação pendente encontrada no extrato.
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              ) : (
                // ─── LAYOUT ORIGINAL DE CARDS PARA RESOLVIDOS / FILTRADOS ───
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  <div className="space-y-4 max-w-5xl mx-auto">
                    {transacoes.map((tx) => {
                      const isDespesa = tx.valor_centavos < 0;
                      return (
                        <div 
                          key={tx.id} 
                          className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col md:flex-row md:items-stretch"
                        >
                          <div className="md:w-1/2 p-4 flex flex-col justify-between border-b md:border-b-0 md:border-r border-slate-200">
                            <div>
                              <div className="flex justify-between items-start gap-2 mb-2">
                                <span className="text-[10px] font-black text-slate-500 font-mono bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                                  FITID: {tx.fitid}
                                </span>
                                <span className="text-[10px] font-bold text-slate-400 font-mono">
                                  {tx.data}
                                </span>
                              </div>
                              <h4 className="text-xs font-bold text-slate-800 break-words mb-2">
                                {tx.descricao}
                              </h4>
                            </div>
                            <div className="flex items-center justify-between mt-4">
                              <span className={`text-sm font-black font-mono ${isDespesa ? "text-rose-600" : "text-emerald-700"}`}>
                                {isDespesa ? "-" : "+"} {formatCentavos(Math.abs(tx.valor_centavos))}
                              </span>
                              <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded border ${
                                tx.status === "conciliado" 
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200" 
                                  : "bg-slate-100 text-slate-500 border-slate-300"
                              }`}>
                                {tx.status}
                              </span>
                            </div>
                          </div>

                          <div className="md:w-1/2 p-4 bg-slate-50/50 flex flex-col justify-center">
                            {tx.status === "conciliado" && (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-emerald-800 text-xs font-semibold">
                                  <Check size={16} className="text-emerald-600" />
                                  <span>Conciliado com Lançamento Ledger:</span>
                                </div>
                                {tx.expand?.lancamento_vinculado && (
                                  <div className="bg-emerald-50/30 border border-emerald-100/70 p-3 rounded-lg text-xs space-y-1">
                                    <div className="font-bold text-slate-800">{tx.expand.lancamento_vinculado.descricao}</div>
                                    <div className="text-[10px] text-slate-500 font-medium">
                                      Categoria: {tx.expand.lancamento_vinculado.expand?.categoria?.nome} | Centro: {tx.expand.lancamento_vinculado.expand?.centro_custo?.nome}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            {tx.status === "ignorado" && (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-slate-500 text-xs font-bold">
                                  <X size={16} className="text-slate-400" />
                                  <span>Transação desconsiderada (Ignorada)</span>
                                </div>
                                <div className="bg-slate-100 border border-slate-200 p-2.5 rounded-lg text-[10px] font-bold text-slate-600 italic">
                                  Motivo: "{tx.motivo_ignorado}"
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {transacoes.length === 0 && (
                      <div className="text-center text-xs text-slate-400 py-12">
                        Nenhuma transação encontrada.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ─── PAINEL DE CONCILIAÇÃO DE GRUPO (RODAPÉ FIXO) (R6) ─── */}
              {filterStatus === "nao_conciliado" && (selectedTxIds.length > 0 || selectedLedgerIds.length > 0) && (
                <div className="bg-slate-900 text-white border-t border-slate-800 p-4 shrink-0 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xl z-20">
                  <div className="flex flex-wrap gap-6 items-center">
                    <div>
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Transações Extrato ({selectedTxIds.length})</span>
                      <span className="text-sm font-black font-mono text-slate-200">
                        R$ {(
                          transacoes
                            .filter(t => selectedTxIds.includes(t.id))
                            .reduce((sum, t) => sum + (t.valor_centavos || 0), 0) / 100
                        ).toFixed(2)}
                      </span>
                    </div>
                    
                    <span className="text-slate-650 font-black text-lg hidden md:inline">↔</span>

                    <div>
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Ledger Selecionados ({selectedLedgerIds.length})</span>
                      <span className="text-sm font-black font-mono text-slate-200">
                        R$ {(
                          ledgerPendentes
                            .filter(l => selectedLedgerIds.includes(l.id))
                            .reduce((sum, l) => sum + (l.valor_centavos || 0), 0) / 100
                        ).toFixed(2)}
                      </span>
                    </div>

                    <div className="border-l border-slate-800 pl-6">
                      <span className="text-[9px] font-black text-amber-400 uppercase tracking-wider block">Taxa de Cartão / Diferença</span>
                      <span className={`text-sm font-black font-mono ${
                        (
                          (ledgerPendentes
                            .filter(l => selectedLedgerIds.includes(l.id))
                            .reduce((sum, l) => sum + (l.valor_centavos || 0), 0) -
                           transacoes
                            .filter(t => selectedTxIds.includes(t.id))
                            .reduce((sum, t) => sum + (t.valor_centavos || 0), 0)) >= 0
                        ) ? "text-emerald-400" : "text-rose-400"
                      }`}>
                        R$ {(
                          (ledgerPendentes
                            .filter(l => selectedLedgerIds.includes(l.id))
                            .reduce((sum, l) => sum + (l.valor_centavos || 0), 0) -
                           transacoes
                            .filter(t => selectedTxIds.includes(t.id))
                            .reduce((sum, t) => sum + (t.valor_centavos || 0), 0)) / 100
                        ).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedTxIds([]);
                        setSelectedLedgerIds([]);
                      }}
                      className="px-4 py-2 text-xs font-bold text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors uppercase tracking-wider"
                    >
                      Limpar
                    </button>
                    
                    {selectedTxIds.length === 1 && selectedLedgerIds.length === 0 && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            const tx = transacoes.find(t => t.id === selectedTxIds[0]);
                            setSelectedTx(tx);
                            const matchedRule = regrasDescritor.find(r => 
                              tx.descricao.toUpperCase().includes(r.padrao.toUpperCase())
                            );
                            if (matchedRule) {
                              setPendenciaCategoria(matchedRule.categoria_sugerida);
                            } else {
                              setPendenciaCategoria("");
                            }
                            setIsPendenciaModalOpen(true);
                          }}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-lg shadow transition-all uppercase tracking-wider"
                        >
                          Lançar Pendência
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const tx = transacoes.find(t => t.id === selectedTxIds[0]);
                            setSelectedTx(tx);
                            setIgnoreReason("");
                            setIsIgnoreModalOpen(true);
                          }}
                          className="px-4 py-2 bg-rose-650 hover:bg-rose-750 text-white font-bold text-xs rounded-lg shadow transition-all uppercase tracking-wider"
                        >
                          Ignorar
                        </button>
                      </>
                    )}

                    <button
                      type="button"
                      onClick={handleConciliarGrupoSubmit}
                      disabled={
                        selectedLedgerIds.length === 0 ||
                        selectedTxIds.length === 0 ||
                        (ledgerPendentes
                          .filter(l => selectedLedgerIds.includes(l.id))
                          .reduce((sum, l) => sum + (l.valor_centavos || 0), 0) -
                         transacoes
                          .filter(t => selectedTxIds.includes(t.id))
                          .reduce((sum, t) => sum + (t.valor_centavos || 0), 0)) < 0
                      }
                      className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-800 disabled:text-slate-600 text-white font-bold text-xs rounded-lg shadow-lg hover:shadow-blue-500/20 transition-all uppercase tracking-widest disabled:cursor-not-allowed"
                    >
                      Conciliar Grupo
                    </button>
                  </div>
                </div>
              )}

            </div>
          )}

          {/* CONTEÚDO DAS PENDÊNCIAS REVERSAS */}
          {activeSubTab === "reversa" && (
            <div className="flex-1 overflow-y-auto p-4">
              <div className="max-w-5xl mx-auto space-y-4">
                <div className="flex gap-2 bg-blue-50 border border-blue-200 text-blue-900 p-3 rounded-lg text-xs font-semibold">
                  <Info size={20} className="shrink-0 text-blue-700 mt-0.5" />
                  <div>
                    <strong>O que é isso?</strong> Esta visão lista todos os lançamentos marcados no sistema como <strong>PAGOS</strong> no mesmo período deste extrato bancário, mas que <strong>não possuem vínculo</strong> com nenhuma transação bancária. Use para checar inconsistências (itens pagos no ERP que não saíram do banco).
                  </div>
                </div>

                <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm bg-white">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Data</th>
                        <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Descrição</th>
                        <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Categoria</th>
                        <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">Centro</th>
                        <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500 text-right">Valor</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-xs font-semibold text-slate-700">
                      {reversas.map((item) => {
                        const isReceita = item.valor_centavos > 0;
                        return (
                          <tr key={item.id} className="hover:bg-slate-50/50">
                            <td className="px-4 py-3 font-mono text-slate-500">{item.data.split(" ")[0]}</td>
                            <td className="px-4 py-3 font-bold text-slate-800">{item.descricao}</td>
                            <td className="px-4 py-3 text-slate-500">{item.expand?.categoria?.nome || "-"}</td>
                            <td className="px-4 py-3 text-slate-550">{item.expand?.centro_custo?.nome || "-"}</td>
                            <td className={`px-4 py-3 font-mono text-right font-bold ${
                              isReceita ? "text-emerald-700" : "text-rose-600"
                            }`}>
                              {isReceita ? "+" : "-"} {formatCentavos(Math.abs(item.valor_centavos))}
                            </td>
                          </tr>
                        );
                      })}

                      {reversas.length === 0 && (
                        <tr>
                          <td colSpan="5" className="px-4 py-12 text-center text-xs font-medium text-slate-400">
                            Nenhum lançamento pendente de conciliação bancária neste período.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

        </div>
      )}

      {/* ─── MODAL: VINCULAR MANUALMENTE (BUSCA LIVRE) ────────────────────────── */}
      {isManualModalOpen && selectedTx && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-2xl overflow-hidden flex flex-col h-[500px]">
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
              <h4 className="font-bold text-sm uppercase tracking-wide">Vinculação Manual de Lançamento</h4>
              <button onClick={() => setIsManualModalOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="p-4 bg-slate-50 border-b border-slate-200 shrink-0 space-y-2 text-xs">
              <div className="font-bold text-slate-400 uppercase tracking-wide">Transação Selecionada no Extrato:</div>
              <div className="flex justify-between items-center">
                <span className="font-black text-slate-800 text-sm break-all">{selectedTx.descricao}</span>
                <span className={`font-black font-mono text-sm ${selectedTx.valor_centavos < 0 ? "text-rose-600" : "text-emerald-700"}`}>
                  {selectedTx.valor_centavos < 0 ? "-" : "+"} {formatCentavos(Math.abs(selectedTx.valor_centavos))}
                </span>
              </div>
              <div className="text-[10px] text-slate-450 font-mono">Data do Extrato: {selectedTx.data}</div>
            </div>

            {/* Corpo de busca */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="text"
                    placeholder="Filtrar por descrição ou valor em R$... (Ex: '634,56')"
                    value={manualSearchQuery}
                    onChange={(e) => setManualSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleManualSearch()}
                    className="w-full pl-9 pr-4 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  />
                </div>
                <button
                  onClick={handleManualSearch}
                  disabled={manualLoading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-colors"
                >
                  Buscar
                </button>
              </div>

              {manualLoading ? (
                <div className="text-center text-xs font-bold text-slate-400 py-10">Buscando lançamentos pagos...</div>
              ) : (
                <div className="space-y-2">
                  <div className="text-[10px] font-black text-slate-400 uppercase tracking-wide">Candidatos no Sistema:</div>
                  <div className="divide-y divide-slate-100 max-h-[180px] overflow-y-auto border border-slate-200 rounded-xl bg-slate-50/20">
                    {manualCandidates.map((c) => (
                      <div
                        key={c.id}
                        onClick={() => {
                          if (window.confirm(`Deseja confirmar a conciliação manual da transação com o lançamento: "${c.descricao}"?`)) {
                            handleConciliarMatch(selectedTx.id, c.id);
                            setIsManualModalOpen(false);
                          }
                        }}
                        className="p-3 hover:bg-blue-50 cursor-pointer flex justify-between items-center transition-colors text-xs font-semibold"
                        title="Clique para vincular este lançamento"
                      >
                        <div className="space-y-0.5">
                          <div className="text-slate-800 font-bold">{c.descricao}</div>
                          <div className="text-[10px] text-slate-400 font-mono">
                            Data: {c.data.split(" ")[0]} | Cat: {c.expand?.categoria?.nome || "-"} | Centro: {c.expand?.centro_custo?.nome || "-"}
                          </div>
                        </div>
                        <span className={`font-black font-mono ${c.valor_centavos < 0 ? "text-rose-600" : "text-emerald-700"}`}>
                          {formatCentavos(c.valor_centavos)}
                        </span>
                      </div>
                    ))}
                    
                    {manualCandidates.length === 0 && (
                      <div className="text-center text-slate-400 text-xs italic py-10">
                        Nenhum candidato encontrado. Altere o termo de busca.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-slate-50 p-4 border-t border-slate-150 flex justify-end shrink-0">
              <button
                onClick={() => setIsManualModalOpen(false)}
                className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-100 border border-slate-200 rounded-lg"
              >
                Voltar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: IGNORAR TRANSAÇÃO (REJEIÇÃO DO EXTRATO) ──────────────────── */}
      {isIgnoreModalOpen && selectedTx && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-md overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
              <h4 className="font-bold text-sm uppercase tracking-wide">Ignorar Transação do Extrato</h4>
              <button onClick={() => setIsIgnoreModalOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleIgnorarSubmit} className="p-5 space-y-4">
              <div className="flex gap-2 bg-amber-50 border border-amber-250 text-amber-900 p-3 rounded-lg text-xs font-semibold">
                <AlertCircle size={20} className="shrink-0 text-amber-700 mt-0.5" />
                <div>
                  <strong>Aviso:</strong> Ignorar uma transação significa que ela não tem correspondência no caixa (Ex: transferência própria ou tarifas de serviços ignoradas). Esta transação ficará arquivada com motivo (Invariante I6).
                </div>
              </div>

              <div>
                <span className="text-[10px] font-bold text-slate-450 block mb-1">Transação:</span>
                <span className="text-xs font-black text-slate-800 block mb-4">{selectedTx.descricao}</span>

                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Motivo do Descarte *</label>
                <input
                  type="text"
                  required
                  value={ignoreReason}
                  onChange={(e) => setIgnoreReason(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  placeholder="EX: Transferência interna, Tarifa bancária já lançada"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsIgnoreModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Voltar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-1 bg-red-650 hover:bg-red-750 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-colors"
                >
                  Ignorar Transação
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── MODAL: CRIAR PENDÊNCIA (FILA DE CONFERÊNCIA) ────────────────────── */}
      {isPendenciaModalOpen && selectedTx && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-md overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
              <h4 className="font-bold text-sm uppercase tracking-wide">Gerar Pendência de Entrada/Saída</h4>
              <button onClick={() => setIsPendenciaModalOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handlePendenciaSubmit} className="p-5 space-y-4">
              <div className="flex gap-2 bg-blue-50 border border-blue-200 text-blue-900 p-3 rounded-lg text-xs font-semibold">
                <Info size={20} className="shrink-0 text-blue-700 mt-0.5" />
                <div>
                  <strong>O que é isso?</strong> Use esta opção quando a transação do banco for legítima mas ainda não estiver registrada no ERP. O sistema enviará o registro para o staging de conferência.
                </div>
              </div>

              <div>
                <span className="text-[10px] font-bold text-slate-450 block mb-1">Transação:</span>
                <span className="text-xs font-black text-slate-800 block mb-4">{selectedTx.descricao}</span>

                <div className="space-y-3">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Categoria Sugerida *</label>
                    <select
                      required
                      value={pendenciaCategoria}
                      onChange={(e) => setPendenciaCategoria(e.target.value)}
                      className="w-full text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-2.5 outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Selecione...</option>
                      {finance.categorias.map((c) => (
                        <option key={c.id} value={c.id}>{c.nome} ({c.tipo})</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Centro de Custo Sugerido *</label>
                    <select
                      required
                      value={pendenciaCentro}
                      onChange={(e) => setPendenciaCentro(e.target.value)}
                      className="w-full text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-2.5 outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Selecione...</option>
                      {finance.centrosCusto.map((c) => (
                        <option key={c.id} value={c.id}>{c.nome}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsPendenciaModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Voltar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-colors"
                >
                  Enviar para Conferência
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* ─── BANNER DE REGRA APRENDIDA (R7) ────────────────────────────────── */}
      {learningRule && (
        <div className="fixed bottom-4 right-4 bg-slate-900 border border-slate-800 text-white rounded-xl shadow-2xl p-4 max-w-sm z-50 animate-slide-up space-y-3">
          <div className="flex justify-between items-start">
            <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest block font-mono">Memorizar Regra</span>
            <button onClick={() => setLearningRule(null)} className="text-slate-400 hover:text-white">
              <X size={16} />
            </button>
          </div>
          <p className="text-xs font-semibold text-slate-305 leading-relaxed">
            Deseja que transações futuras contendo <strong className="text-white bg-slate-800 px-1 py-0.5 rounded">"{learningRule.padrao}"</strong> sejam pré-classificadas como <strong className="text-blue-300">"{learningRule.categoriaNome}"</strong>?
          </p>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setLearningRule(null)}
              className="px-3 py-1.5 text-[10px] font-bold text-slate-400 hover:text-white uppercase tracking-wider"
            >
              Ignorar
            </button>
            <button
              onClick={handleSaveLearnedRule}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider shadow-sm transition-colors"
            >
              Lembrar Regra
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
